/**
 * Pixel Rain Renderer
 * Renders white/grey low-opacity pixel art rain for storm weather
 */

interface RainDrop {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  opacity: number;
  age: number;
  lifetime: number;
}

export class PixelRainRenderer {
  private rainDrops: RainDrop[] = [];
  private graphics: Phaser.GameObjects.Graphics;
  private spawnRate: number = 0.4; // Probability per frame
  private maxDrops: number = 200;
  private windStrength: number = 0;
  private windVariation: number = 0;

  constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics();
    this.graphics.setScrollFactor(0);
    this.graphics.setDepth(120);
  }

  /**
   * Update rain animation
   */
  update(delta: number): void {
    const deltaSeconds = delta / 1000;

    // Update wind (gentle oscillation)
    this.windVariation += 0.02;
    this.windStrength = Math.sin(this.windVariation) * 15;

    // Spawn new drops
    if (this.rainDrops.length < this.maxDrops && Math.random() < this.spawnRate) {
      this.spawnRainDrop();
    }

    // Update existing drops
    for (let i = this.rainDrops.length - 1; i >= 0; i--) {
      const drop = this.rainDrops[i];

      // Apply wind
      drop.vx = -40 + this.windStrength + (Math.random() - 0.5) * 10;

      // Update position
      drop.x += drop.vx * deltaSeconds;
      drop.y += drop.vy * deltaSeconds;
      drop.age += deltaSeconds;

      // Fade out near end of lifetime
      const fadeStart = drop.lifetime * 0.7;
      if (drop.age > fadeStart) {
        drop.opacity = 1 - (drop.age - fadeStart) / (drop.lifetime - fadeStart);
      }

      // Remove if dead or off-screen
      if (drop.age >= drop.lifetime || drop.y > window.innerHeight + 50) {
        this.rainDrops.splice(i, 1);
      }
    }

    // Render all drops
    this.render();
  }

  /**
   * Spawn a new rain drop
   */
  private spawnRainDrop(): void {
    const width = window.innerWidth;

    // Vary drop size for depth perception
    const sizeVariation = Math.random();
    let dropWidth: number;
    let dropHeight: number;
    let baseOpacity: number;

    if (sizeVariation < 0.6) {
      // Small drops (foreground, more opaque)
      dropWidth = 1;
      dropHeight = 3 + Math.random() * 2;
      baseOpacity = 0.5 + Math.random() * 0.25;
    } else if (sizeVariation < 0.9) {
      // Medium drops
      dropWidth = 1;
      dropHeight = 4 + Math.random() * 3;
      baseOpacity = 0.35 + Math.random() * 0.2;
    } else {
      // Large drops (background, more faded)
      dropWidth = 2;
      dropHeight = 5 + Math.random() * 3;
      baseOpacity = 0.2 + Math.random() * 0.15;
    }

    this.rainDrops.push({
      x: Math.random() * (width + 100) - 50,
      y: -20,
      vx: -40 + this.windStrength,
      vy: 300 + Math.random() * 150,
      width: dropWidth,
      height: dropHeight,
      opacity: baseOpacity,
      age: 0,
      lifetime: 2 + Math.random() * 1,
    });
  }

  /**
   * Render all rain drops
   */
  private render(): void {
    this.graphics.clear();

    for (const drop of this.rainDrops) {
      // Determine color based on opacity and variation
      let color: number;
      if (drop.opacity > 0.4) {
        color = 0xffffff; // White for more opaque drops
      } else if (drop.opacity > 0.25) {
        color = 0xe8e8e8; // Light grey
      } else {
        color = 0xd0d0d0; // Medium grey for faded drops
      }

      this.graphics.fillStyle(color, drop.opacity);
      this.graphics.fillRect(drop.x, drop.y, drop.width, drop.height);

      // Add subtle secondary pixel for larger drops (creates slight texture)
      if (drop.width > 1 && Math.random() < 0.3) {
        this.graphics.fillStyle(color, drop.opacity * 0.6);
        this.graphics.fillRect(drop.x + 1, drop.y + 1, 1, Math.max(1, drop.height - 2));
      }
    }
  }

  /**
   * Set the spawn rate (0-1)
   */
  setSpawnRate(rate: number): void {
    this.spawnRate = Math.max(0, Math.min(1, rate));
  }

  /**
   * Set the maximum number of drops
   */
  setMaxDrops(max: number): void {
    this.maxDrops = Math.max(50, max);
  }

  /**
   * Clear all rain drops
   */
  clear(): void {
    this.rainDrops = [];
    this.graphics.clear();
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.clear();
    this.graphics.destroy();
  }
}
